import type { Dispatch } from 'redux';

import {
    createSelectParticipantFailedEvent,
    sendAnalytics
} from '../analytics';
import { _handleParticipantError } from '../base/conference/functions';
import { MEDIA_TYPE } from '../base/media/constants';
import { reportError } from '../base/util';
import {
    getFocusModeVisibleParticipants,
    isParticipantIdAllowedInFocusMode
} from '../focus-mode/functions';
import { shouldDisplayTileView } from '../video-layout';
import { iAmVisitor } from '../visitors/functions';
import {
    getWebinarAudienceLargeVideoParticipants,
    isAudienceStageOnlyModeActive,
    isPresenterHiddenForLocal
} from '../webinar/functions';

import {
    SELECT_LARGE_VIDEO_PARTICIPANT,
    SET_LARGE_VIDEO_FULLSCREEN,
    SET_LARGE_VIDEO_LAST_DOMINANT_SPEAKER,
    SET_NATIVE_FULLSCREEN,
    TOGGLE_SIDEBAR_MENU,
    UPDATE_KNOWN_LARGE_VIDEO_RESOLUTION
} from './actionTypes';

/**
 * Builds the list of participants this client may put on its large video.
 *
 * Three independent rules narrow the same list and all of them have to hold:
 * Focus Mode, the webinar audience's stage-only mode, and the presenter/hidden
 * rule. A tile a viewer is not allowed to see must not reach their stage by any
 * route -- election, a pin, or becoming the dominant speaker.
 *
 * @param {Object} state - The redux state.
 * @private
 * @returns {Array<Object>}
 */
function _getLargeVideoCandidates(state) {
    const base = isAudienceStageOnlyModeActive(state)
        ? getWebinarAudienceLargeVideoParticipants(state)
        : getFocusModeVisibleParticipants(state);

    return base.filter(p => !isPresenterHiddenForLocal(state, p)
        && isParticipantIdAllowedInFocusMode(state, p.id));
}

/**
 * Signals conference to select a participant.
 *
 * @returns {Function}
 */
export function selectParticipant() {
    return (dispatch: Dispatch<any>, getState: Function) => {
        const state = getState();
        const { conference } = state['features/base/conference'];

        if (conference) {
            const ids = (shouldDisplayTileView(state)
                ? _getLargeVideoCandidates(state)
                    .filter(p => !(iAmVisitor(state) && p.local))
                    .map(participant => participant.id)
                : [ state['features/large-video'].participantId ])
                .filter(id => isParticipantIdAllowedInFocusMode(state, id));

            try {
                conference.selectParticipants(ids);
            } catch (err) {
                _handleParticipantError(err);

                sendAnalytics(createSelectParticipantFailedEvent(err));

                reportError(
                    err, `Failed to select participants ${ids.toString()}`);
            }
        }
    };
}

/**
 * Action to select the participant to be displayed in LargeVideo based on the
 * participant id provided. If a participant id is not provided, the LargeVideo
 * participant will be selected based on a variety of factors: If there is a
 * dominant or pinned speaker, or if there are remote tracks, etc.
 *
 * @param {string} participant - The participant id of the user that needs to be
 * displayed on the large video.
 * @param {Object} options - Selection dispatch options.
 * @param {string} options.selectionSource - Selection source identifier.
 * @param {boolean} options.forceSelection - Whether to bypass anti-flicker coalescing.
 * @returns {Function}
 */
export function selectParticipantInLargeVideo(participant: ?string, options: Object = {}) {
    return (dispatch: Dispatch<any>, getState: Function) => {
        const state = getState();

        // A participant this client is not allowed to see under Focus Mode never
        // reaches the stage, however the selection was requested.
        const requestedId = isParticipantIdAllowedInFocusMode(state, participant)
            ? participant
            : undefined;
        const participantId = requestedId ?? _electParticipantInLargeVideo(state);
        const largeVideo = state['features/large-video'];
        const audienceStageOnlyModeActive = isAudienceStageOnlyModeActive(state);

        const participants = _getLargeVideoCandidates(state);
        const visitorMode = iAmVisitor(state);

        // Visitors publish no media, so the local participant must never reach
        // the stage: participants[0] is usually local, which is exactly the
        // empty blue stage a lone visitor saw next to the host's tile.
        const isModeratorPinned = participants.find(p => p.moderatorPin && !(visitorMode && p.local));
        const pinnedParticipant = participants.find(p => p.pinned && !p.moderatorPin
            && !(visitorMode && p.local));
        const dominantSpeaker = participants.find(p => p.dominantSpeaker && !p.local);
        const requestedParticipant = participantId
            && participants.find(p => p.id === participantId && !(visitorMode && p.local));

        let newParticipantId;

        if (pinnedParticipant) {
            newParticipantId = pinnedParticipant.id;
        } else if (isModeratorPinned) {
            newParticipantId = isModeratorPinned.id;
        } else if (dominantSpeaker) {
            newParticipantId = dominantSpeaker.id;
        } else if (requestedParticipant) {
            newParticipantId = requestedParticipant.id;
        } else {
            newParticipantId = participants.find(p => !(visitorMode && p.local) && !p.botType)?.id;
        }

        if (newParticipantId && !isParticipantIdAllowedInFocusMode(state, newParticipantId)) {
            newParticipantId = _electParticipantInLargeVideo(state);
        }

        // Whoever is on the stage right now may have just become invisible to
        // this client (Focus Mode was turned on, or they were demoted), in which
        // case the stage has to be re-elected even if the new pick is unchanged.
        const currentAllowed = isParticipantIdAllowedInFocusMode(state, largeVideo.participantId);
        const currentIsLocalVisitor = visitorMode
            && largeVideo.participantId
            && participants.some(p => p.id === largeVideo.participantId && p.local);

        if (!newParticipantId && (audienceStageOnlyModeActive || currentIsLocalVisitor)
                && largeVideo.participantId) {
            // Nobody may be shown: clear the stage rather than leave a stale tile.
            dispatch({
                type: SELECT_LARGE_VIDEO_PARTICIPANT,
                participantId: undefined
            });
        } else if (newParticipantId
                && (newParticipantId !== largeVideo.participantId || !currentAllowed
                    || currentIsLocalVisitor)) {
            // If whoever is on the stage is gone from the list, dispatch without
            // the anti-flicker delay so the UI cannot stay stuck on them.
            const currentParticipantExists = largeVideo.participantId
                && participants.find(p => p?.id === largeVideo.participantId);
            const forceSelection = Boolean(options.forceSelection)
                || !currentParticipantExists
                || !currentAllowed;
            const selectionSource = options.selectionSource
                || (requestedId ? 'explicit' : 'auto');

            dispatch({
                type: SELECT_LARGE_VIDEO_PARTICIPANT,
                participantId: newParticipantId,
                selectionSource,
                forceSelection
            });
        }

    };
}

/**
 * Remembers the id of the last remote participant who was the dominant speaker.
 * This keeps the large video stable on the person who last spoke once the current
 * dominant speaker stops talking/mutes, instead of jumping to a random participant.
 *
 * @param {?string} participantId - The id of the last dominant speaker (remote), or
 * {@code null}/{@code undefined} to clear it.
 * @returns {{
 *     type: SET_LARGE_VIDEO_LAST_DOMINANT_SPEAKER,
 *     participantId: ?string
 * }}
 */
export function setLargeVideoLastDominantSpeaker(participantId: ?string) {
    return {
        type: SET_LARGE_VIDEO_LAST_DOMINANT_SPEAKER,
        participantId
    };
}

/**
 * Updates the currently seen resolution of the video displayed on large video.
 *
 * @param {number} resolution - The current resolution (height) of the video.
 * @returns {{
 *     type: UPDATE_KNOWN_LARGE_VIDEO_RESOLUTION,
 *     resolution: number
 * }}
 */
export function updateKnownLargeVideoResolution(resolution: number) {
    return {
        type: UPDATE_KNOWN_LARGE_VIDEO_RESOLUTION,
        resolution
    };
}

/**
 * Toggles the sidebar menu.
 *
 * @param {boolean} isSidebarMenuOpen - Whether the sidebar is open.
 * @returns {{
 *     type: TOGGLE_SIDEBAR_MENU,
 *     isSidebarMenuOpen: boolean
 * }}
 */
export function toggleSidebarMenu(isSidebarMenuOpen: boolean) {
    return {
        type: TOGGLE_SIDEBAR_MENU,
        isSidebarMenuOpen
    };
}

/**
 * Toggles the native fullscreen mode which hides NavigationBar,
 * Filmstrip, SideMenuBar, Toolbox, and tooltips.
 *
 * @param {boolean} isNativeFullScreen - Whether native fullscreen is active.
 * @returns {{
 *     type: SET_NATIVE_FULLSCREEN,
 *     isNativeFullScreen: boolean
 * }}
 */
export function setNativeFullScreen(isNativeFullScreen: boolean) {
    return {
        type: SET_NATIVE_FULLSCREEN,
        isNativeFullScreen
    };
}

/**
 * Returns the most recent existing remote video track.
 *
 * @param {Track[]} tracks - All current tracks.
 * @param {Object} state - The redux state, used to skip the participants Focus
 * Mode hides from this client.
 * @private
 * @returns {(Track|undefined)}
 */
function _electLastVisibleRemoteVideo(tracks, state) {
    // First we try to get most recent remote video track.
    for (let i = tracks.length - 1; i >= 0; --i) {
        const track = tracks[i];

        if (!track.local && track.mediaType === MEDIA_TYPE.VIDEO
                && isParticipantIdAllowedInFocusMode(state, track.participantId)) {
            return track;
        }
    }
}

/**
 * Returns the identifier of the participant who is to be on the stage and
 * should be displayed in {@code LargeVideo}.
 *
 * @param {Object} state - The Redux state from which the participant to be
 * displayed in {@code LargeVideo} is to be elected.
 * @private
 * @returns {(string|undefined)}
 */
function _electParticipantInLargeVideo(state) {
    // 1. If a participant is pinned, they will be shown in the LargeVideo (
    //    regardless of whether they are local or remote).
    const participants = _getLargeVideoCandidates(state);
    const _iAmVisitor = iAmVisitor(state);

    // A visitor publishes no media, so their own tile must never be elected.
    const isModeratorPinned = participants.find(p => p.moderatorPin && !(_iAmVisitor && p.local));
    const pinnedParticipant = participants.find(p => p.pinned && !(_iAmVisitor && p.local));
    let participant = participants && participants.length && (pinnedParticipant || isModeratorPinned);
    let id = participant && participant.id;


    if (!id) {
        if (pinnedParticipant) {
            return pinnedParticipant.id;
        } else if (isModeratorPinned) {
            return isModeratorPinned.id;
        }

        // 2. No participant is pinned so get the dominant speaker. But the
        //    local participant won't be displayed in LargeVideo even if she is
        //    the dominant speaker.
        participant = participants.find(p => p.dominantSpeaker && !p.local);
        id = participant && participant.id;

        if (!id) {
            // 3. There is no current dominant speaker. Prefer the last remote participant
            //    who was the dominant speaker (if they are still in the conference and not a
            //    bot), so the large video stays stable on the person who last spoke instead
            //    of immediately jumping to a random participant or the local video.
            const lastDominantSpeakerId = state['features/large-video']?._lastDominantSpeakerId;
            const lastDominantSpeaker = lastDominantSpeakerId
                && participants.find(p => p.id === lastDominantSpeakerId && !p.local && !p.botType);

            if (lastDominantSpeaker) {
                return lastDominantSpeaker.id;
            }

            // 4. There is no dominant speaker so select the remote participant
            //    who last had visible video.
            const tracks = state['features/base/tracks'];
            const videoTrack = _electLastVisibleRemoteVideo(tracks, state);

            id = videoTrack && videoTrack.participantId;

            if (!id) {
                // 4. It's possible there is no participant with visible video.
                //    This can happen for a number of reasons:
                //    - there is only one participant (i.e. the local user),
                //    - other participants joined with video muted.
                //    As a last resort, pick the last participant who joined the
                //    conference (regardless of whether they are local or
                //    remote).
                //
                // HOWEVER: We don't want to show poltergeist or other bot type participants on stage
                // automatically, because it's misleading (users may think they are already
                // joined and maybe speaking).
                for (let i = participants?.length; i > 0 && !participant; i--) {
                    const p = participants[i - 1];

                    !p.botType && !(_iAmVisitor && p.local) && (participant = p);
                }

                id = participant && participant.id;
            }
        }
    }

    return id;
}

/**
 * Sets the large video fullscreen state.
 *
 * @param {boolean} largevideofullscreen - Whether large video is fullscreen.
 * @returns {{
 *     type: SET_LARGE_VIDEO_FULLSCREEN,
 *     largevideofullscreen: boolean
 * }}
 */
export function setLargeVideoFullscreen(largevideofullscreen: boolean) {
    return {
        type: SET_LARGE_VIDEO_FULLSCREEN,
        largevideofullscreen
    };
}
